import { Badge, Table, TableContainer, Tbody, Td, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FilterBar, FilterDateRange, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, useAppToast, useCopyToClipboard, useHasRole, formatPhone } from '../../../helpers'
import { VideoQuizService } from '../../../services/VideoQuiz/VideoQuiz.service'
import { IVideoQuiz, IListFilters } from './models'
import { VideoQuizViewModal } from './VideoQuizViewModal'

const INITIAL_FILTERS: IListFilters = {
  search: '',
  status: 'all',
  created_at: { from: '', to: '' }
}

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Finalizado' },
  { value: 'in-progress', label: 'Em andamento' }
]

export default function VideoQuizPage() {
  const service = new VideoQuizService()
  const [isDeleting, setIsDeleting] = useState(false)
  const toast = useAppToast()
  const { copy } = useCopyToClipboard()
  const [videoQuizes, setVideoQuizes] = useState<IVideoQuiz[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [length, setLength] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [draw, setDraw] = useState(0)
  const [modalId, setModalId] = useState<number>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const [deleteId, SetDeleteId] = useState<number>()
  const [filters, setFilters] = useState<IListFilters>(INITIAL_FILTERS)
  const isDirty =
    filters.search !== '' || filters.status !== 'all' || filters.created_at.from !== '' || filters.created_at.to !== ''

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilters = (patch: Partial<IListFilters>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }
  const hasRole = useHasRole()
  const permissions = {
    deleteVideoQuizzes: hasRole('delete_video_quizzes')
  }

  const handleDelete = async () => {
    if (deleteId === undefined) {
      return
    }

    setIsDeleting(true)
    try {
      await service.delete(deleteId)
      toast.success('Video quiz excluído')
      deleteDialogIsClose()
      loadVideoQuizes()
    } catch (error) {
      toast.error('Erro ao excluir o video quiz', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const loadVideoQuizes = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const leadsList = await service.list({
        draw,
        length,
        start: (page - 1) * length,
        order: [{ column: 0, dir: 'desc' }],
        filters
      })

      setDraw(draw + 1)

      setTotalPages(Math.ceil(leadsList.recordsFiltered / length))

      setVideoQuizes(leadsList.data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as respostas', error)
    } finally {
      setLoading(false)
    }
  }

  // `search` chega debounced do `FilterSearch`, então isto não dispara mais uma requisição por tecla.
  useEffect(() => {
    loadVideoQuizes()
  }, [page, length, filters])

  const handleOpenModal = async (id: number) => {
    setModalId(id)
    setIsModalOpen(true)
  }

  const handleDeleteDialog = (id: number) => {
    deleteDialogOnOpen()
    SetDeleteId(id)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calcPercentage = (value1: number, value2: number): string => {
    if (value1 === 0 && value2 === 0) return '0.00'

    return ((value1 / (value1 + value2)) * 100).toFixed(2)
  }

  return (
    <>
      <PageTitle title="Video Quiz" description="Respostas enviadas pelos participantes do quiz" />
      <FilterBar onClear={() => updateFilters(INITIAL_FILTERS)} isDirty={isDirty}>
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="Buscar participante"
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(status) => updateFilters({ status: status as IListFilters['status'] })}
          options={STATUS_OPTIONS}
          // "Todos" aqui é um valor de verdade que a API espera, não a ausência de filtro.
          emptyValue="all"
        />
        <FilterDateRange
          label="Data de criação"
          value={{ start: filters.created_at.from, end: filters.created_at.to }}
          onChange={(date) => updateFilters({ created_at: { from: date.start, to: date.end } })}
        />
      </FilterBar>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>E-mail</Th>
              <Th>Telefone</Th>
              <Th>Progresso</Th>
              <Th>Nota</Th>
              <Th>Iniciado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={8} />
            ) : loadError ? (
              <TableErrorRow colSpan={8} entity="respostas" feminine onRetry={loadVideoQuizes} />
            ) : videoQuizes.length === 0 ? (
              <TableEmptyRow colSpan={8} entity="resposta" feminine />
            ) : (
              videoQuizes.map((workSubmission) => (
                <Tr key={workSubmission.id}>
                  <Td>{workSubmission.id}</Td>
                  <Td>{workSubmission.name}</Td>
                  <Td>{workSubmission.email}</Td>
                  <Td>{formatPhone(workSubmission.phone)}</Td>
                  <Td>
                    {Math.round((workSubmission.current_step / 6) * 100)}%
                    {workSubmission.current_step === 6 ? (
                      <Badge colorScheme="green" ml="2">
                        Finalizado
                      </Badge>
                    ) : (
                      <Badge colorScheme="yellow" ml="2">
                        Em andamento
                      </Badge>
                    )}
                  </Td>
                  <Td>{workSubmission.correct_answers_count * 2}</Td>
                  <Td>{formatDateTime(workSubmission.created_at)}</Td>
                  <Td>
                    <ActionButton action="view" onClick={() => handleOpenModal(workSubmission.id)} />
                    {permissions.deleteVideoQuizzes && (
                      <ActionButton action="delete" onClick={() => handleDeleteDialog(workSubmission.id)} />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={length}
        onPageSizeChange={(pageSize) => {
          setLength(pageSize)
          setPage(1)
        }}
      />

      <VideoQuizViewModal id={modalId} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <ConfirmDialog
        isOpen={deleteDialogIsOpen}
        onClose={deleteDialogIsClose}
        onConfirm={handleDelete}
        action="delete"
        entity="video quiz"
        isLoading={isDeleting}
      />
      <ActionFab
        action="link"
        label="Copiar link para área de transferência"
        colorScheme="magenta"
        variant="solid"
        onClick={() => copy('https://abra.com.br/video-quiz', { entity: 'link' })}
      />
    </>
  )
}
